import React, { useState } from "react";
import styled from "styled-components";
import classnames from "classnames";
import { getAssetsUrl, cropImageSize, sortDietIcons, isMealSoldCheck } from "../../../utils/functions";
import Controls from "./../../molecules/Controls";
import ModalWindow from "./../../molecules/Modal";
import { ReactComponent as Star } from "./../../atoms/icons/star.svg";
import { ReactComponent as Heart } from "./../../atoms/icons/heart.svg";
import { ReactComponent as Excluded } from "./../../atoms/icons/excluded.svg";
import { ReactComponent as EmptyMeal } from "./../../atoms/icons/empty-dinner.svg";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  margin: 0;
  padding: 15px 6px 25px;

  &.sold,
  &.excluded {
    .meal-item__img,
    .meal-item__tags,
    .meal-item__title,
    .meal-item__icons,
    .meal-item__controls {
      opacity: 0.3;
    }
  }

  &:nth-child(2n + 3) {
    &::before {
      content: "";
      position: absolute;
      width: calc(100% - 15px);
      border-top: 1px solid #e6e6e6;
      transform: translate(0, -10px);
    }
  }

  @media (min-width: 540px) {
    width: 33.33%;

    &:nth-child(2n + 3) {
      &::before {
        display: none;
      }
    }

    &:nth-child(3n + 4) {
      &::before {
        display: block;
        content: "";
        position: absolute;
        width: calc(100% - 15px);
        border-top: 1px solid #e6e6e6;
        transform: translate(0, -10px);
      }
    }
  }
`;

const ImageWrap = styled.div`
  position: relative;
  min-height: 50%;
  text-align: center;

  b {
    text-shadow: 0 2px 6px #444;
  }

  &:hover {
    div.img-overlay {
      opacity: 1;
      visibility: visible;
      transition: 0.3s ease all;
    }

    div.img-overlay--sold {
      opacity: 0;
      visibility: hidden;
      transition: 0.1s ease all;
    }

    div:not(.img-overlay):not(.meal-item__oos) {
      opacity: 0;
      visibility: hidden;
      transition: 0.3s ease all;
    }

    .empty-image,
    .meal-item__flag {
      opacity: 1 !important;
      visibility: visible !important;
    }

    img {
      filter: brightness(50%);
      transition: 0.3s ease all;
    }
  }
`;

const Image = styled.img`
  width: 100%;
  height: auto;
  filter: brightness(100%);
  transition: 0.3s ease all;
`;

const EmptyImageWrap = styled.div`
  position: relative;
  width: calc(100% - 30px);
  margin: 15px;
  padding-bottom: calc(100% - 30px);
  background: #e6e6e6;
  border-radius: 50%;

  svg {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    margin: auto;
  }
`;

const Flag = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: var(--color-primary);
  font-size: 11px;
  line-height: 24px;
  color: #fff;
  text-align: center;
  user-select: none;

  @media (min-width: 768px) {
    width: 32px;
    height: 32px;
    top: 8px;
    right: 20px;
    font-size: 14px;
    line-height: 32px;
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 30px;
  font-size: 16px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: -0.3px;
  text-align: center;
  color: #fff;
  text-align: center;
  opacity: 0;
  visibility: hidden;
  transition: 0.3s ease all;
  cursor: pointer;
  user-select: none;

  &.img-overlay--sold {
    opacity: 1;
    visibility: visible;
  }
`;

const OutOfStock = styled.div`
  position: absolute;
  right: 50%;
  bottom: 10px;
  padding: 3px 8px 2px 8px;
  border-radius: 7px;
  background: var(--color-primary);
  font-size: 12px;
  color: #fff;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.2;
  text-transform: uppercase;
  text-align: center;
  transform: translate(50%, 0);
  user-select: none;
`;

const Tags = styled.div`
  display: flex;
  position: absolute;
  top: 20px;
  left: 0;
  opacity: 1;
  visibility: visible;
  transition: 0.3s ease all;
  flex-wrap: wrap;
`;

const TagItem = styled.span`
  display: flex;
  align-items: center;
  height: 26px;
  margin-right: 6px;
  padding: 2px 7px;
  border-radius: 7px;
  border: 1px solid #fff;
  font-size: 12px;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.67;
  letter-spacing: normal;
  text-align: center;
  color: #fff;

  svg {
    align-self: center;
    margin-right: 5px;
  }
  @media (max-width: 383px) {
    height: 26px;
    line-height: normal;
  }
`;

const NewTag = styled(TagItem)`
  background: var(--color-secondary);
  @media (max-width: 383px) {
    height: 24px;
  }
`;

const FavoriteTag = styled(TagItem)`
  background: #ff8b00;
  @media (max-width: 383px) {
    height: 24px;
  }
`;

const ExcludedTag = styled(TagItem)`
  background: var(--color-primary);
`;

const Icons = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 4px;
  margin-bottom: 20px;

  .meal-item__icons-item {
    position: relative;
  }

  .meal-item__icons-item:hover .meal-item__icons-tooltip {
    visibility: visible;
    opacity: 1;
  }

  .meal-item__icons-tooltip {
    position: absolute;
    left: 50%;
    bottom: 100%;
    margin-bottom: 5px;
    padding: 3px 16px;
    background-color: #5c5c5c;
    border-radius: 3px;
    font-size: 12px;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.67;
    color: #fff;
    text-align: center;
    transform: translate(-50%, 0);
    white-space: nowrap;
    opacity: 0;
    transition: opacity 0.3s;

    &::after {
      content: "";
      position: absolute;
      left: 50%;
      top: 100%;
      width: 0;
      height: 0;
      border-style: solid;
      border-width: 3px 4px 0 4px;
      border-color: #5c5c5c transparent transparent transparent;
      transform: translate(-50%, 0);
    }
  }

  img {
    width: 24px;
    margin: 0 5px;
  }
`;

const Title = styled.h3`
  margin: 4px 0 8px 0;
  font-size: 14px;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.43;
  letter-spacing: normal;
  text-align: center;

  @media (min-width: 990px) {
    font-size: 16px;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: -0.3px;
  }
`;

function MealItem(props) {
  const {
    id,
    title,
    image,
    images,
    tags,
    quantitySelected,
    excluded,
    showQuantityBadge,
    mealBoughtQuantity,
    stockCapacity,
    view,
    deliveryItem,
    charge,
    mealChange,
    linkToCollection = null,
    mealsCollectionButtonLabel = null,
  } = props;
  const [modalIsOpen, setModalIsOpen] = useState(false);
  let _charge = { ...charge };

  const openModal = () => {
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
  };

  const handleQuantityChange = (quantity) => {
    let type;

    tags.forEach((tag) => {
      if (tag.toLowerCase().includes("breakfast")) {
        type = "breakfast";
        return false;
      } else if (tag.toLowerCase().includes("entrée")) {
        type = "entree";
        return false;
      }
    });
    mealChange({
      id: props.id,
      quantity: quantity,
      title: props.title,
      image: props.image,
      type: type,
      tags: [...props.tags],
      sku: props.variants.length ? props.variants[0].sku : "",
      variants: props.variants,
      images: [...props.images],
      handle: props.handle,
      frequence: props.frequence,
      mealBoughtQuantity: props.mealBoughtQuantity,
      stockCapacity: props.stockCapacity,
    });
  };

  const iconsUrlsGeneration = function () {
    let icons = [];
    let sortedIcons = [];
    const storeUrl = getAssetsUrl();

    tags.forEach(function (tag) {
      const tagName = tag.toLowerCase();
      let fileName = tagName.split("_");
      if (tagName.includes("icon") && fileName.length > 1) {
        icons.push({
          url: storeUrl + fileName[1].replace(" ", "-") + ".svg?v=1",
          title: tag.split("_")[1],
          tag: tag,
        });
      }
    });

    sortedIcons = sortDietIcons(icons);

    return (
      <Icons className="meal-item__icons">
        {sortedIcons.map((icon, index) => {
          return (
            <span className="meal-item__icons-item" key={index}>
              <b className="meal-item__icons-tooltip">{icon.title}</b>
              <img src={icon.url} loading="lazy" decoding="async" alt={icon.title} key={index} />
            </span>
          );
        })}
      </Icons>
    );
  };

  const isSold = deliveryItem
    ? false
    : isMealSoldCheck({
        id: id,
        mealBoughtQuantity: mealBoughtQuantity,
        stockCapacity: stockCapacity,
        quantity: quantitySelected,
        charge: _charge,
      });

  const iconsUrls = iconsUrlsGeneration();
  const showQuantityFlag = typeof showQuantityBadge !== "undefined" ? showQuantityBadge : false;
  const optimizedImage = cropImageSize(image, "540");

  return (
    <Wrap
      className={classnames(
        "meal-item",
        { excluded: excluded },
        { sold: isSold && !deliveryItem },
        { "meal-item--compact": view === "compact" }
      )}
      data-id={id}
    >
      <ImageWrap className="meal-item__image-wrap">
        {showQuantityFlag && <Flag className="meal-item__flag">{quantitySelected}</Flag>}
        {image.length > 0 ? (
          <Image
            srcSet={`${image}&width=163 163w, ${image}&width=200 200w, ${image}&width=238 238w, ${image}&width=273 273w, ${image}&width=305 305w, ${image}&width=476 476w, ${image}&width=546 546w, ${image}&width=610 610w`}
            sizes="(min-width: 540px) 305px, (min-width: 990px) 238px, (min-width: 1280px) 273px, 238px"
            src={optimizedImage}
            className="meal-item__img"
            loading="lazy"
            decoding="async"
            width="300"
            height="300"
            alt={title}
          />
        ) : (
          <EmptyImageWrap className="empty-image">
            <EmptyMeal />
          </EmptyImageWrap>
        )}
        <Tags className="meal-item__tags">
          {excluded ? (
            <ExcludedTag className="meal-item__tags-item--excluded">
              <Excluded />
              <span>Excluded ingredients</span>
            </ExcludedTag>
          ) : null}
          {tags.map((tagItem, index) => {
            let tag = tagItem.toLowerCase();

            if (tag.includes("favorite")) {
              return (
                <FavoriteTag key={index}>
                  <Heart /> <span>{tagItem}</span>
                </FavoriteTag>
              );
            } else if (tag.includes("new")) {
              return (
                <NewTag key={index}>
                  <Star /> <span>{tagItem}</span>
                </NewTag>
              );
            } else return null;
          })}
        </Tags>
        <ImageOverlay className="img-overlay" onClick={openModal}>
          <b>See Ingredients and Nutrition</b>
        </ImageOverlay>
        {isSold && !deliveryItem && (
          <OutOfStock className="meal-item__oos">
            <b>Sold out</b>
          </OutOfStock>
        )}
      </ImageWrap>
      <Title className="meal-item__title">{title}</Title>
      {iconsUrls}
      {!deliveryItem && (
        <Controls quantityChange={handleQuantityChange} quantitySelected={quantitySelected} excluded={excluded} isSold={isSold} />
      )}

      <ModalWindow
        productId={id}
        images={images}
        modalIsOpen={modalIsOpen}
        closeModal={closeModal}
        linkToCollection={linkToCollection}
        mealsCollectionButtonLabel={mealsCollectionButtonLabel}
      />
    </Wrap>
  );
}

export default MealItem;
